import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useStore } from "react-redux";
import FemaleProvider from "../assets/Doctor_Female.jpg";
import MaleProvider from "../assets/Doctor_Male_96.png";
import MalePatient from "../assets/Patient_Male_96.png";
import FemalePatient from "../assets/Patient_Female_96.png";
import styles from "../modals/ResourceViewerModal.module.css";
import { fetchProviderByReference } from "../services/providerService";
import { fetchOrganizationByReference } from "../services/organizationService";
import { fetchPatientByReference } from '../services/patientService';
import { fetchEncounterByReference } from '../services/encounterService';
import { resolveName, hydrateFhirGraph } from "../utils/messageUtils";
import { renderReference } from "../utils/renderUtils";

// -------------------------
// Preload image helper
// -------------------------
const preloadImage = (url, fallback) => {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(url);
        img.onerror = () => resolve(fallback);
        img.src = url;
    });
};

// -------------------------
// CodeableConcept display
// -------------------------
const ccToString = (cc) => {
    if (!cc) return "N/A";
    if (cc.text) return cc.text;
    const coding = cc.coding?.[0];
    return coding ? coding.display || coding.code || "Unknown" : "Unknown";
};


// -------------------------
// CareTeamCardExtended
// -------------------------
const CareTeamCardExtended = ({ resource, handleShowNested, fhirClient }) => {
    const store = useStore();
    const { getState } = store;

    // -------------------------
    // Participant Providers
    // -------------------------
    const [participantDataMap, setParticipantDataMap] = useState({});
    const practitionerRefs = (resource.participant || [])
        .map((p) => p.member?.reference)
        .filter((r) => r && r.startsWith("Practitioner/"));

    useEffect(() => {
        if (!practitionerRefs.length) return;
        let mounted = true;

        const loadProviders = async () => {
            const newMap = {};
            for (const ref of practitionerRefs) {
                try {
                    const provider = await fetchProviderByReference(ref, fhirClient, store);
                    if (provider) newMap[ref] = provider;
                } catch (err) {
                    console.error("Failed to fetch provider:", ref, err);
                }
            }
            if (mounted) setParticipantDataMap((prev) => ({ ...prev, ...newMap }));
        };

        loadProviders();
        return () => { mounted = false; };
    }, [JSON.stringify(practitionerRefs)]);

    // -------------------------
    // Organization References
    // -------------------------
 
    useEffect(() => {
        console.log("CareTeamCardExtended useEffect - resource changed:", resource);
        if (!resource) return;
        const orgRefs = [];

        // managingOrganization
        (resource.managingOrganization || []).forEach((org) => {
            if (org?.reference?.startsWith("Organization/")) orgRefs.push(org.reference);
        });

        // participants referencing Organization
        (resource.participant || []).forEach((p) => {
            const ref = p.member?.reference;
            if (ref?.startsWith("Organization/")) orgRefs.push(ref);
        });

        orgRefs.forEach((ref) => {
            fetchOrganizationByReference(ref, fhirClient, store);
        });
    }, [resource]);

    // -------------------------
    // Subject (Patient) Handling
    // -------------------------
    const [subject, setSubject] = useState(null);
    const [subjectImg, setSubjectImg] = useState(null);
    const [subjectLoading, setSubjectLoading] = useState(true);

    useEffect(() => {

        hydrateFhirGraph(resource, fhirClient, store);

        const loadSubject = async () => {
            const patientRef = resource.subject?.reference;
            if (!patientRef) {
                setSubjectImg(FemalePatient);
                setSubjectLoading(false);
                return;
            }

            try {
                await fetchPatientByReference(patientRef, fhirClient, store);
                const patient = getState()?.patients?.[patientRef.split("/")[1]];
                setSubject(patient || null);

                const fallback = patient?.gender?.toLowerCase() === "female" ? FemalePatient : MalePatient;
                const url = patient?.photo?.[0]?.url || fallback;
                const loadedUrl = await preloadImage(url, fallback);
                setSubjectImg(loadedUrl);
            } catch (err) {
                console.error("Failed to fetch subject:", err);
                setSubjectImg(FemalePatient);
            } finally {
                setSubjectLoading(false);
            }
        };

        loadSubject();
    }, [resource]);

    // -------------------------
    // Encounter Handling
    // -------------------------
    const [encounter, setEncounter] = useState(null);

    useEffect(() => {
        const loadEncounter = async () => {
            const encounterRef = resource.encounter?.reference;
            if (!encounterRef) return;

            try {
                await fetchEncounterByReference(encounterRef, fhirClient, store);
                const enc = getState()?.encounters?.[encounterRef.split("/")[1]];
                setEncounter(enc || null);
            } catch (err) {
                console.error("Failed to fetch encounter:", err);
            }
        };

        loadEncounter();
    }, [resource]);

    // -------------------------
    // Participant Portrait Component
    // -------------------------
    const ParticipantPortrait = ({ pdata }) => {
        const [img, setImg] = useState(null);
        const [loading, setLoading] = useState(true);

        useEffect(() => {
            const load = async () => {
                if (!pdata) {
                    setImg(MaleProvider);
                    setLoading(false);
                    return;
                }
                const fallback = pdata.gender?.toLowerCase() === "female" ? FemaleProvider : MaleProvider;
                const url = pdata.photo?.[0]?.url || fallback;
                const loadedUrl = await preloadImage(url, fallback);
                setImg(loadedUrl);
                setLoading(false);
            };
            load();
        }, [pdata]);

        return (
            <div style={{ position: "relative" }}>
                {loading && <Spinner animation="border" />}
                {img && (
                    <img
                        src={img}
                        alt="Participant Portrait"
                        style={{
                            width: "60px",
                            height: "60px",
                            objectFit: "cover",
                            borderRadius: "6px",
                            border: "1px solid #ccc",
                            backgroundColor: "#f8f9fa",
                            display: loading ? "none" : "block"
                        }}
                    />
                )}
            </div>
        );
    };

    // -------------------------
    // Basic Data
    // -------------------------
    const identifiers = resource.identifier || [];
    const categories = resource.category || [];
    const period = resource.period || {};
    const participants = resource.participant || [];
    const reasonCodes = resource.reasonCode || [];
    const reasonRefs = resource.reasonReference || [];
    const managingOrgs = resource.managingOrganization || [];
    const telecom = resource.telecom || [];
    const subjectName = resolveName(resource.subject?.reference, store) || "Unknown";
    const encounterName = resolveName(resource.encounter?.reference, store) || resource.encounter?.reference;

    // -------------------------
    // Render
    // -------------------------
    return (
        <div className="p-4 border rounded shadow-sm bg-white">
            {/* SUBJECT PORTRAIT */}
            <div className="d-flex align-items-center mb-3">
                <button
                    onClick={() => handleShowNested?.(subject, subjectName)}
                    style={{ padding: 0, border: "none", background: "none", cursor: "pointer", marginRight: "1rem" }}
                >
                    <div style={{ position: "relative" }}>
                        {subjectLoading && <Spinner animation="border" />}
                        {subjectImg && (
                            <img
                                src={subjectImg}
                                alt="Subject Portrait"
                                style={{
                                    width: "100px",
                                    height: "100px",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                    border: "1px solid #ccc",
                                    backgroundColor: "#f8f9fa",
                                    display: subjectLoading ? "none" : "block"
                                }}
                            />
                        )}
                    </div>
                </button>
                <div>
                    <div className="mb-1 text-muted"><strong>Team For:</strong> {subjectName}</div>
                    <div className="mb-1 text-muted"><strong>Gender:</strong> {subject?.gender || "Unknown"}</div>
                    <div className="mb-1 text-muted"><strong>DOB:</strong> {subject?.birthDate || "Unknown"}</div>
                    <div className="mb-1 text-muted"><strong>Age:</strong> {subject?.birthDate ? (() => {
                        const birth = new Date(subject.birthDate);
                        const today = new Date();
                        let age = today.getFullYear() - birth.getFullYear();
                        const m = today.getMonth() - birth.getMonth();
                        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
                        return age;
                    })() : "Unknown"}</div>
                </div>
            </div>

            {/* CARE TEAM DETAILS */}
            <div className={styles.sectionHeader}>Care Team Details</div>
            <div className={styles.indented}>
                <div className={styles.sectionContent}>
                    <div className="mb-1"><strong>Name:</strong> {resource.name || "N/A"}</div>
                    <div className="mb-1"><strong>Status:</strong> {resource.status || "Unknown"}</div>
                    <div className="mb-1"><strong>Period:</strong> {period.start || "?"} — {period.end || "?"}</div>
                    <div className="mb-1">
                        <strong>Encounter:</strong>{" "}
                        {renderReference(resource.encounter?.reference, encounterName, handleShowNested)}
                    </div>
                </div></div>

            {/* IDENTIFIERS */}
            {identifiers.length > 0 && (
                <>
                    <div className={styles.sectionHeader}>Identifiers</div>
                    <div className={styles.indented}>
                        <div className={styles.sectionContent}>
                            {identifiers.map((id, idx) => (<div key={idx} className="mb-1"><strong>{id.system}:</strong> {id.value}</div>))}
                        </div></div>
                </>
            )}

            {/* CATEGORIES */}
            {categories.length > 0 && (
                <>
                    <div className={styles.sectionHeader}>Categories</div>
                    <div className={styles.indented}>
                        <div className={styles.sectionContent}>
                            {categories.map((cat, idx) => (<div key={idx}>{ccToString(cat)}</div>))}
                        </div></div>
                </>
            )}

            {/* PARTICIPANTS */}
            <div className={styles.sectionHeader}>Participants</div>
            <div className={styles.indented}>
                {participants.length === 0 && <div className="text-muted">No team members listed.</div>}
                {participants.map((p, idx) => {
                    const ref = p.member?.reference;
                    const pdata = ref ? participantDataMap[ref] : null;
                    const pname = resolveName(ref, store) || ref || "Unknown";

                    const onBehalfRef = p.onBehalfOf?.reference;
                    const onBehalfName = resolveName(onBehalfRef, store) || onBehalfRef || "Unknown";

                    return (
                        <div key={idx} className="d-flex align-items-center mb-3 gap-3">
                            <ParticipantPortrait pdata={pdata} />
                            <div>
                                <div><strong>Member:</strong>{" "} {renderReference(ref, pname, handleShowNested)}</div>
                                <div><strong>Role:</strong> {p.role?.map(ccToString).join(", ") || "N/A"}</div>
                                <div><strong>On Behalf Of:</strong>{" "} {renderReference(onBehalfRef, onBehalfName, handleShowNested)}</div>
                                {p.period && <div><strong>Period:</strong> {p.period.start || "?"} — {p.period.end || "?"}</div>}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* REASONS */}
            {reasonCodes.length > 0 && (
                <>
                    <div className={styles.sectionHeader}>Reasons</div>
                    <div className={styles.indented}>
                        <div className={styles.sectionContent}>
                            {reasonCodes.map((rc, idx) => (<div key={idx}>{ccToString(rc)}</div>))}
                        </div></div>
                </>
            )}

            {reasonRefs.length > 0 && (
                <>
                    <div className={styles.sectionHeader}>Reason References</div>
                    <div className={styles.indented}>
                          <div className={styles.sectionContent}>
                        {reasonRefs.map((rr, idx) => (
                            <div key={idx}>{renderReference(rr.reference, rr.reference, handleShowNested)}</div>
                        ))}
                    </div></div>
                </>
            )}

            {/* MANAGING ORGANIZATIONS */}
            {managingOrgs.length > 0 && (
                <>
                    <div className={styles.sectionHeader}>Managing Organizations</div>
                    <div className={styles.indented}>
                         <div className={styles.sectionContent}>
                        {managingOrgs.map((org, idx) => {
                            const orgName = resolveName(org.reference, store) || org.reference;
                            return (
                                <div key={idx}>{renderReference(org.reference, orgName, handleShowNested)}</div>
                            );
                        })}
                    </div></div>
                </>
            )}

            {/* TELECOM */}
            {telecom.length > 0 && (
                <>
                    <div className={styles.sectionHeader}>Contact</div>
                    <div className={styles.indented}>
                        {telecom.map((t, idx) => (<div key={idx}><strong>{t.system}:</strong> {t.value} ({t.use})</div>))}
                    </div>
                </>
            )}
        </div>
    );
};

export default CareTeamCardExtended;
